import { useUrlGenerator } from '@folklore/routes';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import usePage from '../../hooks/usePage';
import { getImageAtSize } from '../../lib/utils';

import { useTheme, useThemeClassName } from '../../contexts/ThemeContext';
import Blocks from '../blocks/Blocks';
import Button from '../buttons/Button';
import PrizeForm from '../forms/Prize';
import PageTitle from '../partials/PageTitle';
import Prize from '../partials/Prize';
import PrizeCard from '../partials/PrizeCard';

import styles from '../../../styles/pages/account-prize.module.scss';

function AccountPrizePage() {
    const { page = null, refetch: reload } = usePage();
    const route = useUrlGenerator();
    const themeClassName = useThemeClassName(styles);
    const { name: themeName = null } = useTheme();

    const { prize = null, title, headerImage = null, blocks = null } = page || {};
    const { prize: prizeObject, shouldFillClaimForm = false, claimed = false } = prize || {};
    const {
        type,
        name,
        description,
        date,
        image,
        badge,
        claimInstructions = null,
        instructions = null,
    } = prizeObject || {};

    const rtgUnclaimedNode = useRef(null);
    const rtgClaimedNode = useRef(null);

    const onPrizeClaimed = useCallback(() => {
        reload();
    }, [reload]);

    const { url: imageUrl, alt: imageAlt } = getImageAtSize(headerImage, 'medium', false) || {};

    const renderPhysicalMessage = useCallback(() => {
        switch (themeName) {
            case 'iga':
                return (
                    <FormattedMessage
                        defaultMessage="Fill out this form so that a member of our team can contact you to give you your prize."
                        description="Prize claim form description"
                    />
                );
            case 'voisin':
                return (
                    <FormattedMessage
                        defaultMessage="Fill out this form, we will get back to you shortly."
                        description="Prize claim form description - Voisin variant"
                    />
                );
            default:
                return (
                    <FormattedMessage
                        defaultMessage="Complete the form and we will get back to you shortly."
                        description="Prize claim form description"
                    />
                );
        }
    }, [themeName]);

    return (
        <div className={classNames([styles.container, themeClassName, 'account-prize'])}>
            <div className={styles.header}>
                {imageUrl !== null ? (
                    <img src={imageUrl} alt={imageAlt} className={styles.image} />
                ) : (
                    <PageTitle className={styles.title} title={title} />
                )}
                <div className={styles.subtitle}>{date}</div>
            </div>
            <Button className={styles.backToAccountButton} href={route('account')}>
                <span className={styles.buttonInner}>
                    <span className={styles.icon}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </span>
                    <span className={styles.label}>
                        <FormattedMessage
                            defaultMessage="Back to account"
                            description="Button label"
                        />
                    </span>
                </span>
            </Button>
            {blocks !== null && blocks.length > 0 ? (
                <Blocks blocks={blocks} isContainer className={styles.blocks} />
            ) : null}
            <div className={styles.inner}>
                {shouldFillClaimForm && !claimed ? (
                    <div
                        className={classNames([styles.section, styles.unclaimed])}
                        ref={rtgUnclaimedNode}
                    >
                        <PrizeCard
                            className={styles.prizeCard}
                            image={image}
                            name={name}
                            description={description}
                            badge={badge}
                        />
                        <div className={styles.formContainer}>
                            {claimInstructions !== null ? (
                                <div
                                    className={styles.instructions}
                                    dangerouslySetInnerHTML={{ __html: claimInstructions }}
                                />
                            ) : (
                                <div className={styles.instructions}>
                                    {type === 'physical' ? (
                                        renderPhysicalMessage()
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="Complete the form to claim your prize."
                                            description="Prize claim form description"
                                        />
                                    )}
                                </div>
                            )}
                            <PrizeForm
                                className={styles.form}
                                selectPlaceholderClassName={styles.selectPlaceholder}
                                prize={prize}
                                onSuccess={onPrizeClaimed}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={styles.section} ref={rtgClaimedNode}>
                        {instructions !== null ? (
                            <div
                                className={styles.instructions}
                                dangerouslySetInnerHTML={{ __html: instructions }}
                            />
                        ) : null}
                        {type === 'physical' && instructions === null ? (
                            <div className={styles.instructions}>
                                <FormattedMessage
                                    defaultMessage="Thank you, we will get back to you shortly."
                                    description="Prize claim form description"
                                />
                            </div>
                        ) : null}
                        <Prize
                            className={styles.prize}
                            prize={prize}
                            onPrizeClaim={onPrizeClaimed}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default AccountPrizePage;
